import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Leaf } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export default function Header() {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const base = `/${lang}`;
  const isHome = location.pathname === base || location.pathname === `${base}/`;

  const handleAnchor = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (!isHome) {
      navigate(base);
      setTimeout(() => scrollToSection(id), 60);
    } else {
      scrollToSection(id);
    }
  };

  const switchLang = (target) => {
    if (target === lang) return;
    const path = location.pathname;
    let newPath;
    if (path.startsWith("/fr")) newPath = path.replace(/^\/fr/, `/${target}`);
    else if (path.startsWith("/en")) newPath = path.replace(/^\/en/, `/${target}`);
    else newPath = `/${target}`;
    navigate(newPath + location.search + location.hash, {
      state: { skipScroll: true },
    });
  };

  const navItems = [
    { key: "home", href: base, type: "route" },
    { key: "sectors", id: "sectors", type: "anchor" },
    { key: "analysis", id: "circuit", type: "anchor" },
    { key: "governance", id: "transparency", type: "anchor" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
        <NavLink
          to={base}
          data-testid="header-brand-link"
          className="flex items-center gap-3"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-emerald-600 text-white">
            <Leaf className="h-6 w-6" strokeWidth={2.25} />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-[15px] font-bold tracking-tight text-slate-900">
              {t("brand.name")}
            </span>
            <span className="text-[11px] font-medium uppercase tracking-widest text-emerald-700">
              {t("brand.since")}
            </span>
          </span>
        </NavLink>

        <nav
          data-testid="header-navigation"
          className="hidden items-center gap-8 lg:flex"
        >
          {navItems.map((item) =>
            item.type === "route" ? (
              <NavLink
                key={item.key}
                to={item.href}
                end
                data-testid={`header-nav-${item.key}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-tight ${
                    isActive
                      ? "text-emerald-700"
                      : "text-slate-700 hover:text-emerald-700"
                  }`
                }
              >
                {t(`header.nav.${item.key}`)}
              </NavLink>
            ) : (
              <a
                key={item.key}
                href={`#${item.id}`}
                data-testid={`header-nav-${item.key}`}
                onClick={handleAnchor(item.id)}
                className="text-sm font-medium tracking-tight text-slate-700 hover:text-emerald-700"
              >
                {t(`header.nav.${item.key}`)}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden items-center rounded-full border border-slate-200 bg-slate-50 p-1 md:flex"
            data-testid="language-switcher"
          >
            <button
              type="button"
              onClick={() => switchLang("fr")}
              data-testid="lang-toggle-fr"
              className={`rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${
                lang === "fr"
                  ? "bg-emerald-600 text-white"
                  : "text-slate-600 hover:text-emerald-700"
              }`}
            >
              FR
            </button>
            <button
              type="button"
              onClick={() => switchLang("en")}
              data-testid="lang-toggle-en"
              className={`rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${
                lang === "en"
                  ? "bg-emerald-600 text-white"
                  : "text-slate-600 hover:text-emerald-700"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="#newsletter"
            onClick={handleAnchor("newsletter")}
            data-testid="header-cta-newsletter"
            className="hidden rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 sm:inline-flex"
          >
            {t("header.nav.newsletter")}
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-hamburger-button"
            aria-label={open ? t("header.menu_close") : t("header.menu_open")}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 text-slate-800 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-slate-200 bg-white lg:hidden"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6">
            {navItems.map((item) =>
              item.type === "route" ? (
                <NavLink
                  key={item.key}
                  to={item.href}
                  end
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.key}`}
                  className="rounded-md px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-50"
                >
                  {t(`header.nav.${item.key}`)}
                </NavLink>
              ) : (
                <a
                  key={item.key}
                  href={`#${item.id}`}
                  onClick={handleAnchor(item.id)}
                  data-testid={`mobile-nav-${item.key}`}
                  className="rounded-md px-3 py-3 text-base font-medium text-slate-800 hover:bg-slate-50"
                >
                  {t(`header.nav.${item.key}`)}
                </a>
              )
            )}
            <a
              href="#newsletter"
              onClick={handleAnchor("newsletter")}
              data-testid="mobile-cta-newsletter"
              className="mt-2 rounded-md bg-emerald-600 px-3 py-3 text-center text-base font-semibold text-white"
            >
              {t("header.nav.newsletter")}
            </a>
            <div className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-2">
              <button
                type="button"
                onClick={() => {
                  switchLang("fr");
                  setOpen(false);
                }}
                data-testid="mobile-lang-fr"
                className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold ${
                  lang === "fr" ? "bg-emerald-600 text-white" : "text-slate-700"
                }`}
              >
                Français
              </button>
              <button
                type="button"
                onClick={() => {
                  switchLang("en");
                  setOpen(false);
                }}
                data-testid="mobile-lang-en"
                className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold ${
                  lang === "en" ? "bg-emerald-600 text-white" : "text-slate-700"
                }`}
              >
                English
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
